// src/utils/request.js

import axios from 'axios';
import { message } from 'antd';
import appStore from '../stores/app/store';  // 从上一级目录导入
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { clearAuthCookies } from '../utils/cookie';

// ---------------- Visitor ID 管理 ----------------
const VISITOR_ID_KEY = 'external_visitorid';

// 获取 Cookie
function getCookie(name) {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
    return match ? decodeURIComponent(match[2]) : null;
}

// 设置 Cookie
function setVisitorCookie(name, value, days = 30) {
    if (typeof document === 'undefined') return;
    let cookie = `${name}=${encodeURIComponent(value)};`;
    // 设置主域名，支持跨子域共享
    const host = window.location.host;
    if (host.indexOf('127.0.0.1') < 0 && host.indexOf('localhost') < 0) {
        const mainHost = host.slice(host.indexOf('.') + 1);
        cookie += `domain=${mainHost};`;
    }
    if (days) {
        const exp = new Date();
        exp.setTime(exp.getTime() + days * 24 * 60 * 60 * 1000);
        cookie += `expires=${exp.toUTCString()};`;
    }
    cookie += 'path=/;';
    document.cookie = cookie;
}

// 设置 visitorid（同时存储到 Cookie 和 localStorage）
export function setVisitorId(visitorid) {
    if (!visitorid) return;
    // 优先使用已存在的 visitorid
    const existingId = getCookie(VISITOR_ID_KEY) || localStorage.getItem(VISITOR_ID_KEY);
    const finalId = existingId || visitorid;
    
    setVisitorCookie(VISITOR_ID_KEY, finalId, 30);
    if (typeof localStorage !== 'undefined') {
        localStorage.setItem(VISITOR_ID_KEY, finalId);
    }
}

// 获取 visitorid（优先 Cookie，其次 localStorage）
export function getVisitorId() {
    const cookieValue = getCookie(VISITOR_ID_KEY);
    if (cookieValue) return cookieValue;
    
    if (typeof localStorage !== 'undefined') {
        return localStorage.getItem(VISITOR_ID_KEY);
    }
    return null;
}

// 生成并初始化指纹ID（应用启动时调用一次）
let fingerprintPromise = null;
export async function initVisitorId() {
    // 如果已存在 visitorid，直接返回
    const existingId = getVisitorId();
    if (existingId) return existingId;
    
    // 避免重复初始化
    if (fingerprintPromise) return fingerprintPromise;
    
    fingerprintPromise = (async () => {
        try {
            const fp = await FingerprintJS.load();
            const result = await fp.get();
            const visitorId = result.visitorId;
            setVisitorId(visitorId);
            return visitorId;
        } catch (e) {
            console.warn('[Fingerprint] 生成指纹ID失败：', e);
            return null;
        }
    })();
    
    return fingerprintPromise;
}

// 应用加载时自动初始化
if (typeof window !== 'undefined') {
    // 使用 requestIdleCallback 或 setTimeout 延迟初始化，避免阻塞首屏
    if (window.requestIdleCallback) {
        window.requestIdleCallback(() => initVisitorId());
    } else {
        setTimeout(() => initVisitorId(), 500);
    }
}

// ---------------- IQOO-HMAC-SHA256 签名 ----------------
// 环境密钥映射：默认使用测试环境，生产通过 REACT_APP_ENV=production 切换
const APP_ID = '1002'; // Web 端固定使用 1002
const APP_SECRET =process.env.REACT_APP_APP_SECRET

// phpUrlEncode
function phpUrlEncode(str) {
    return encodeURIComponent(str)
        .replace(/!/g, '%21')
        .replace(/'/g, '%27')
        .replace(/\(/g, '%28')
        .replace(/\)/g, '%29')
        .replace(/\*/g, '%2A');
}

// HMAC-SHA256 -> Base64（使用 Web Crypto API）
async function hmacSHA256Base64(key, data) {
    const enc = new TextEncoder();
    const cryptoKey = await crypto.subtle.importKey(
        'raw',
        enc.encode(key),
        { name: 'HMAC', hash: 'SHA-256' },
        false,
        ['sign']
    );
    const signature = await crypto.subtle.sign('HMAC', cryptoKey, enc.encode(data));
    const bytes = new Uint8Array(signature);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
}

// 构造 GET 查询串
function buildQueryString(obj = {}) {
    const transformed = transformObject(obj);
    const keys = Object.keys(transformed).sort();
    let qs = '';
    for (const key of keys) {
        const val = transformed[key];
        const encodedKey = phpUrlEncode(key);
        const encodedValue = phpUrlEncode(val === undefined || val === null ? '' : String(val));
        qs += `${encodedKey}=${encodedValue}&`;
    }
    return qs.slice(0, -1);
}

// 生成 SIGN 值：'IQOO-HMAC-SHA256 ' + authorization(...)
async function makeAuthorization(method = 'GET', path = '', query = {}, payload = undefined) {
    const timestamp = Math.floor(Date.now() / 1000);
    const upperMethod = (method || 'GET').toUpperCase();
    const queryString = upperMethod === 'GET' ? buildQueryString(query || {}) : '';
    // 与 Apifox 公共签名脚本一致：multipart/form-data 不把表单内容加入签名正文。
    const isFormData = typeof FormData !== 'undefined' && payload instanceof FormData;
    const httpPayload = upperMethod === 'GET' || isFormData ? '' : JSON.stringify(payload || {});
    const strToSign = `${upperMethod}&${path}&${queryString}&${httpPayload}&appid=${APP_ID}&timestamp=${timestamp}`;
    const signature = await hmacSHA256Base64(APP_SECRET, strToSign);
    
    // 调试信息
    // console.log('🔐 [SIGN DEBUG]', {
    //     method: upperMethod,
    //     path,
    //     query,
    //     payload,
    //     queryString,
    //     httpPayload,
    //     strToSign,
    //     APP_ID,
    //     APP_SECRET: APP_SECRET,
    //     timestamp,
    //     signature: signature
    // });
    
    return `appid=${APP_ID},timestamp=${timestamp},signature=${signature}`;
}

// 数据重置函数，空对象/数组返回null
function resetData(data) {
    if (!data) return null;
    if (Array.isArray(data)) {
        return data.length === 0 ? null : data;
    }
    if (typeof data === 'object' && Object.keys(data).length === 0) return null;
    return data;
}

// 对象参数扁平化处理
function transformObject(originalObj) {
    const transformedObj = {};
    for (const key in originalObj) {
        if (typeof originalObj[key] === 'object' && originalObj[key] !== null) {
            for (const nestedKey in originalObj[key]) {
                transformedObj[`${key}[${nestedKey}]`] = originalObj[key][nestedKey];
            }
        } else {
            transformedObj[key] = originalObj[key];
        }
    }
    return Object.keys(transformedObj).reduce((acc, key) => {
        if (transformedObj[key] !== undefined) acc[key] = transformedObj[key];
        return acc;
    }, {});
}

// 全局错误提示，使用 antd 的 message 组件
function showToast(msg) {
    message.error(msg)
}

// API基础配置
// 统一使用 /api 作为 baseURL
const baseURL = process.env.REACT_APP_API_BASE_URL || '/api';
const instance = axios.create({
    baseURL,
    withCredentials: true,
    timeout: 10000,
    validateStatus(status) {
        return status >= 200 && status <= 504;
    },
});

// 请求拦截
instance.interceptors.request.use(
    async config => {
        // 统一处理method大写
        if (config.method) config.method = config.method.toUpperCase();
        // token: 优先从 cookie 中读取 access_token，其次回退到 localStorage
        function getCookie(name) {
            const match = document.cookie.match(new RegExp('(^| )' + name + '=([^;]+)'));
            return match ? decodeURIComponent(match[2]) : null;
        }

        const cookieToken = typeof document !== 'undefined' ? getCookie('access_token') : null;
        const token = cookieToken;
        if (token) config.headers.Authorization = `Bearer ${token}`;
        // 添加 VIVO-HMAC-SHA256 签名
        try {
            let path = config.url || '';
            const method = config.method || 'GET';
            let params = { ...(config.params || {}) };
            const data = config.data || {};
            
            // 解析 URL 中的查询参数，将其合并到 params 中
            if (path.includes('?')) {
                const [basePath, queryString] = path.split('?');
                path = basePath;
                if (queryString) {
                    const urlParams = new URLSearchParams(queryString);
                    urlParams.forEach((value, key) => {
                        // URL 中的参数优先级低于 config.params
                        if (!(key in params)) {
                            params[key] = value;
                        }
                    });
                }
            }
            
            const auth = await makeAuthorization(method, path, params, data);
            config.headers.SIGN = `VIVO-HMAC-SHA256 ${auth}`;
            // 获取 platform 变量
            const platform = appStore.platform;  // 'pc' 或 'h5'
            // console.log('当前平台:', platform);
            config.headers['X-Platform'] = platform;
            // 添加 X-Visitor 头
            const visitorid = getVisitorId();
            if (visitorid) {
                config.headers['X-Visitor'] = visitorid;
            }
            
        } catch (e) {
            // 失败不阻断请求
            console.warn('[SIGN] 生成失败：', e);
        }
        return config;
    },
    error => {
        showToast('请求错误');
        return Promise.reject(error);
    }
);

// 清除登录相关 cookie 并跳转登录
function clearCookiesAndRedirectLogin() {
    clearAuthCookies();
    
    // 清除 localStorage
    if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('token');
        localStorage.removeItem('userInfo');
    }
    
    // 跳转到 OAuth 登录
    // if (typeof window !== 'undefined') {
    //     const API_BASE_URL = process.env.REACT_APP_API_BASE || '';
    //     const OAUTH_URL = `${API_BASE_URL}/api/v3/users/vivo/oauth`;
    //     window.location.href = OAUTH_URL;
    // }
}

   

// 响应拦截
instance.interceptors.response.use(
    res => {
        // 兼容后端返回大写Code/Data/Message
        const { data, status, statusText } = res;
        const code = data.code !== undefined ? data.code : data.Code;
        const msg = data.msg !== undefined ? data.msg : data.Message;
        const meta = data.meta !== undefined ? data.meta : (data.Meta || '');
        const realData = data.data !== undefined ? data.data : data.Data;
        // 业务code处理
        switch (code) {
            case -3001: // 未登录，清除 cookie 并跳转登录
                // 检查是否已经在跳转过程中
                const redirectingKey = 'login_redirecting';
                const currentPageId = window.location.pathname;
                const pageSpecificKey = `${redirectingKey}_${currentPageId}`;
                
                const isProcessing = sessionStorage.getItem(pageSpecificKey);
                if (!isProcessing) {
                    // 设置标记，有效期 3 秒（根据页面不同）
                    sessionStorage.setItem(pageSpecificKey, 'true');
                    setTimeout(() => {
                        sessionStorage.removeItem(pageSpecificKey);
                    }, 3000);
                    
                    console.log('触发登录跳转，标记：', pageSpecificKey);
                    // 执行跳转
                    clearCookiesAndRedirectLogin();
                } else {
                    console.log('已在跳转过程中，跳过重复跳转');
                }
                break;
            case 401: // INVALID_TOKEN
                // clearLoginStatus && clearLoginStatus();
                // LoginHelper && LoginHelper.saveAndLogin && LoginHelper.saveAndLogin();
                break;
            case 404:
                // Router.replace && Router.replace({ url: '/404' });
                break;
            default:
                break;
        }
        if (status === 200) {
            return Promise.resolve({
                code,
                data: resetData(realData),
                msg,
                meta
            });
        }
        return Promise.resolve({
            code: status,
            data: null,
            msg: statusText,
        });
    },
    err => {
        // 请求可通过 config.skipErrorToast=true 跳过全局错误提示，由调用方自行处理错误文案
        const skipErrorToast = err?.config?.skipErrorToast;
        if (!skipErrorToast) {
            showToast(err.message || '接口错误');
        }
        return Promise.resolve({
            code: -1,
            data: null,
            msg: err.message || '网络发生错误',
            errorType: 'network',
        });
    }
);

// API方法封装
const api = {
    get(url, params = {}, config = {}) {
        return instance.get(url, { params, ...config });
    },
    post(url, data = {}, config = {}) {
        return instance.post(url, data, config);
    },
    put(url, data = {}, config = {}) {
        return instance.put(url, data, config);
    },
    delete(url, data = {}, config = {}) {
        return instance.delete(url, { data, ...config });
    }
};

export * from './api';
export default api;
